import { getFragmentData, graphql } from '@e-socna/data-layer';
import { EMPTY_ID } from '@e-socna/empty-client';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLQuery } from '../../../hooks/useGraphQLQuery.hook';

import {
  Access_ActionAccess_Fragment,
  Access_Answer_Fragment,
} from '../fragments/access-answer.fragment';
import {
  allGatesOf,
  gateOf,
  stateChangeGateOf,
} from '../utils/access-gate.util';
import type { AccessGate } from '../types/access-gate.type';

import { useRefetchOnChange } from './useRefetchOnChange.hook';

export const MissionAccess_QueryDocument = graphql(`
  query MissionAccess_Query($id: Id!) {
    findOneMission(id: $id) {
      _id
      access {
        update: can(scope: "update") {
          ...Access_Answer_Fragment
        }
        updatePricing: can(scope: "updatePricing") {
          ...Access_Answer_Fragment
        }
        updateQuotationRelatedFields: can(
          scope: "updateQuotationRelatedFields"
        ) {
          ...Access_Answer_Fragment
        }
        initBusinessEvent: can(scope: "initBusinessEvent") {
          ...Access_Answer_Fragment
        }
        initQuotation: can(scope: "initQuotation") {
          ...Access_Answer_Fragment
        }
        initReport: can(scope: "initReport") {
          ...Access_Answer_Fragment
        }
        initAccounting: can(scope: "initAccounting") {
          ...Access_Answer_Fragment
        }
        updateQuotation: can(scope: "quotation:update") {
          ...Access_Answer_Fragment
        }
        updateQuotationDocument: can(scope: "quotation:updateRelatedDocument") {
          ...Access_Answer_Fragment
        }
        updateAccounting: can(scope: "accounting:update") {
          ...Access_Answer_Fragment
        }
        actions {
          ...Access_ActionAccess_Fragment
        }
        quotationActions: actions(part: "quotation") {
          ...Access_ActionAccess_Fragment
        }
        accountingActions: actions(part: "accounting") {
          ...Access_ActionAccess_Fragment
        }
      }
    }
  }
`);

export interface MissionAccess {
  // When the answers were received (0 before): a page decides on answers received after it opened.
  readonly answeredAt: number;
  readonly update: AccessGate;
  readonly updatePricing: AccessGate;
  // The fields of the mission its quotation reads, frozen once it is submitted (state only).
  readonly updateQuotationRelatedFields: AccessGate;
  readonly updateAddress: AccessGate;
  readonly updateCollaborationLink: AccessGate;
  readonly initBusinessEvent: AccessGate;
  readonly initQuotation: AccessGate;
  readonly initReport: AccessGate;
  readonly initAccounting: AccessGate;
  readonly updateQuotation: AccessGate;
  // The content of the quotation document, editable in the states of its workflow allowing it.
  readonly updateQuotationDocument: AccessGate;
  readonly updateAccounting: AccessGate;
  readonly changeState: AccessGate;
  readonly changeQuotationState: AccessGate;
  readonly changeAccountingState: AccessGate;
}

// What the user may do on a mission and its quotation and accounting; `version` is the mission as
// loaded, so that a write refreshes it.
export const useMissionAccess = (
  missionId: Id | undefined,
  version?: unknown,
): MissionAccess => {
  const { data, refetch, dataUpdatedAt } = useGraphQLQuery(
    MissionAccess_QueryDocument,
    { id: missionId ?? EMPTY_ID },
    { enabled: !!missionId },
  );

  useRefetchOnChange(refetch, version);

  const access = data?.findOneMission.access;
  const update = gateOf(
    getFragmentData(Access_Answer_Fragment, access?.update),
  );
  const updateQuotationRelatedFields = gateOf(
    getFragmentData(
      Access_Answer_Fragment,
      access?.updateQuotationRelatedFields,
    ),
  );

  return {
    answeredAt: dataUpdatedAt,
    update,
    updateQuotationRelatedFields,
    updateAddress: allGatesOf(update, updateQuotationRelatedFields),
    updateCollaborationLink: allGatesOf(update, updateQuotationRelatedFields),
    updatePricing: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.updatePricing),
    ),
    initBusinessEvent: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initBusinessEvent),
    ),
    initQuotation: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initQuotation),
    ),
    initReport: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initReport),
    ),
    initAccounting: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.initAccounting),
    ),
    updateQuotation: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.updateQuotation),
    ),
    updateQuotationDocument: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.updateQuotationDocument),
    ),
    updateAccounting: gateOf(
      getFragmentData(Access_Answer_Fragment, access?.updateAccounting),
    ),
    changeState: stateChangeGateOf(
      getFragmentData(Access_ActionAccess_Fragment, access?.actions),
    ),
    changeQuotationState: stateChangeGateOf(
      getFragmentData(Access_ActionAccess_Fragment, access?.quotationActions),
    ),
    changeAccountingState: stateChangeGateOf(
      getFragmentData(Access_ActionAccess_Fragment, access?.accountingActions),
    ),
  };
};
